import { forwardRef, useCallback, useImperativeHandle } from 'react';
import classnames from 'classnames';
import {
  UploadFile,
  FileUploaderAdapter,
  FileUploaderRef,
  FileValidationError,
  FileUploaderLabels,
  UploadMode,
} from './types';
import { createUploadFile } from './utils/normalizeFile';
import { useUploadQueue } from './hooks/useUploadQueue';
import { FileDropzone } from './FileDropzone';
import { FileList } from './FileList';
import s from './FileUploader.module.scss';

export type FileUploaderProps = {
  value: UploadFile[];
  onChange: (files: UploadFile[]) => void;
  adapter: FileUploaderAdapter;
  uploadMode?: UploadMode;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  accept?: string[];
  disabled?: boolean;
  disableDropzone?: boolean;
  readOnly?: boolean;
  label?: string;
  hint?: string;
  error?: string;
  onValidationError?: (errors: FileValidationError[]) => void;
  labels?: FileUploaderLabels;
};

export const FileUploader = forwardRef<FileUploaderRef, FileUploaderProps>(
  (
    {
      value,
      onChange,
      adapter,
      uploadMode = 'auto',
      multiple = false,
      maxFiles,
      maxSize = 2 * 1024 * 1024,
      accept,
      disabled = false,
      disableDropzone = false,
      readOnly = false,
      label,
      hint,
      error,
      onValidationError,
      labels,
    },
    ref
  ) => {
    const { uploadAll, retryFile, registerRawFile } = useUploadQueue({
      files: value,
      onChange,
      adapter,
      uploadMode,
    }) as ReturnType<typeof useUploadQueue> & { registerRawFile: (key: string, file: File) => void };

    // ── Expose imperative API via ref ───────────────────────────────────────
    useImperativeHandle(ref, () => ({
      upload: uploadAll,
      reset: () => onChange([]),
    }));

    // ── Handle newly dropped / selected files ───────────────────────────────
    const handleFilesAccepted = useCallback(
      (rawFiles: File[]) => {
        const newUploadFiles = rawFiles.map((f) => {
          const uploadFile = createUploadFile(f);
          // Register raw File in the queue so upload/retry can access it
          registerRawFile(uploadFile._key, f);
          return uploadFile;
        });

        // Always merge — never replace existing list
        onChange([...value, ...newUploadFiles]);
      },
      [value, onChange, registerRawFile]
    );

    // ── Handle validation errors from the dropzone ──────────────────────────
    const handleValidationError = useCallback(
      (errors: FileValidationError[]) => {
        onValidationError?.(errors);
      },
      [onValidationError]
    );

    // ── Remove a file from the list ─────────────────────────────────────────
    const handleRemove = useCallback(
      (key: string) => {
        const file = value.find((f) => f._key === key);

        // If the server assigned an id and the adapter supports deletion, call it
        if (file?.id != null && adapter.deleteFile) {
          adapter.deleteFile(file.id).catch(() => {
            // Deletion errors are non-fatal — file is removed from UI regardless
          });
        }

        onChange(value.filter((f) => f._key !== key));
      },
      [value, onChange, adapter]
    );

    // ── Retry a failed file ─────────────────────────────────────────────────
    const handleRetry = useCallback(
      (key: string) => {
        retryFile(key);
      },
      [retryFile]
    );

    const hasError = Boolean(error);

    return (
      <div className={s.file_uploader}>
        {label && (
          <span className={s.file_uploader__label}>{label}</span>
        )}

        <div className={s.file_uploader__dropzone_wrap}>
          <FileDropzone
            onFilesAccepted={handleFilesAccepted}
            onValidationError={handleValidationError}
            multiple={multiple}
            maxFiles={maxFiles}
            maxSize={maxSize}
            accept={accept}
            disabled={disabled || readOnly || disableDropzone}
            currentFileCount={value.length}
            hasError={hasError}
            dropText={labels?.dropText}
            browseText={labels?.browseText}
            hint={labels?.dropzoneHint}
          />

          {hasError && (
            <span
              className={classnames(s.file_uploader__message, s['file_uploader__message--error'])}
              role="alert"
            >
              {error}
            </span>
          )}
          {!hasError && hint && (
            <span className={s.file_uploader__message}>{hint}</span>
          )}
        </div>

        {value.length > 0 && (
          <div className={s.file_uploader__file_list}>
            <FileList
              files={value}
              onRemove={handleRemove}
              onRetry={handleRetry}
              disabled={disabled}
              readOnly={readOnly}
              labels={labels}
            />
          </div>
        )}
      </div>
    );
  }
);

FileUploader.displayName = 'FileUploader';
